<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Admin Panel</h1>
            <p class="text-blue-100">Manage users and view platform stats</p>
          </div>
        </div>
      </div>
    </div>

    <admin-tab-nav :active-tab="activeTab" @navigate="handleTabNav" />

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 text-left">
      <!-- Overview tab -->
      <div v-if="activeTab === 'overview'" class="space-y-6">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <admin-stat-card label="Total Users" icon="group" color="blue" :value="adminStats?.totalUsers ?? 0" :loading="adminStatsLoading && !adminStats" />
          <admin-stat-card label="Admins" icon="admin_panel_settings" color="yellow" :value="adminStats?.totalAdmins ?? 0" :loading="adminStatsLoading && !adminStats" />
          <admin-stat-card label="New This Week" icon="trending_up" color="green" :value="adminStats?.newUsersThisWeek ?? 0" :loading="adminStatsLoading && !adminStats" />
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <admin-breakdown-card
            title="Leading Faculties"
            icon="account_balance"
            :items="adminStats?.facultyBreakdown ?? []"
            :loading="adminStatsLoading && !adminStats"
          />
          <admin-breakdown-card
            title="Study Year"
            icon="school"
            :items="adminStats?.studyYearBreakdown ?? []"
            :loading="adminStatsLoading && !adminStats"
          />
          <admin-breakdown-card
            title="Campuses"
            icon="location_on"
            :items="adminStats?.campusBreakdown ?? []"
            :loading="adminStatsLoading && !adminStats"
          />
        </div>
      </div>

      <!-- Users tab -->
      <admin-user-grid
        v-if="activeTab === 'users'"
        v-model:search-query="searchQuery"
        :users="adminUsers"
        :loading="adminUsersLoading"
        :error="adminUsersError"
        :has-more="adminUsersHasMore"
        :active-filter-count="activeFilterCount"
        :current-user-id="user?.uid"
        @toggle-role="handleToggleRole"
        @retry="loadUsers(true)"
        @load-more="loadMoreUsers"
        @open-filters="showFilterSheet = true"
      />
    </div>
  </div>

  <admin-user-filter-sheet
    v-model="showFilterSheet"
    :filters="filters"
    :faculties="facultyOptions"
    :campuses="campusOptions"
    @apply="applyFilters"
  />

  <app-confirm-dialog
    v-model="showRoleConfirm"
    :title="confirmTitle"
    :message="confirmMessage"
    :type="pendingRoleChange && pendingRoleChange.newRole === 'admin' ? 'info' : 'warning'"
    icon="admin_panel_settings"
    :confirm-text="pendingRoleChange && pendingRoleChange.newRole === 'admin' ? 'Make Admin' : 'Remove Admin'"
    @confirm="confirmRoleChange"
  />

  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import AppConfirmDialog from '@/components/common/AppConfirmDialog.vue';
import AdminTabNav from '@/components/admin/AdminTabNav.vue';
import AdminStatCard from '@/components/admin/AdminStatCard.vue';
import AdminBreakdownCard from '@/components/admin/AdminBreakdownCard.vue';
import AdminUserGrid from '@/components/admin/AdminUserGrid.vue';
import AdminUserFilterSheet from '@/components/admin/AdminUserFilterSheet.vue';
import { mapState } from 'vuex';

export default {
  components: {
    AppHeader,
    BottomNav,
    AppConfirmDialog,
    AdminTabNav,
    AdminStatCard,
    AdminBreakdownCard,
    AdminUserGrid,
    AdminUserFilterSheet
  },
  data() {
    return {
      activeTab: 'overview',
      searchQuery: '',
      filters: {
        role: '',
        faculty: '',
        studyYear: '',
        campus: ''
      },
      showFilterSheet: false,
      showRoleConfirm: false,
      pendingRoleChange: null,
      searchDebounceTimer: null
    };
  },
  computed: {
    ...mapState(['user', 'globalSettings', 'curricula', 'adminUsers', 'adminUsersLoading', 'adminUsersHasMore', 'adminUsersError', 'adminStats', 'adminStatsLoading']),
    activeFilterCount() {
      return Object.values(this.filters).filter(Boolean).length;
    },
    facultyOptions() {
      // The faculty field is stored against the `curricula` collection's
      // faculty keys, not globalSettings.universities.*.faculties (that list
      // is only used for display copy elsewhere and can be out of sync).
      return Object.keys(this.curricula || {}).sort();
    },
    campusOptions() {
      const set = new Set();
      Object.values(this.globalSettings?.universities || {}).forEach((uni) => {
        (uni.campuses || []).forEach((c) => set.add(c));
      });
      return [...set].sort();
    },
    confirmTitle() {
      if (!this.pendingRoleChange) return '';
      return this.pendingRoleChange.newRole === 'admin' ? 'Make Admin' : 'Remove Admin';
    },
    confirmMessage() {
      if (!this.pendingRoleChange) return '';
      const displayName = this.pendingRoleChange.name || this.pendingRoleChange.email;
      return this.pendingRoleChange.newRole === 'admin'
        ? `Give ${displayName} admin access? They will be able to manage users and platform settings.`
        : `Remove admin access from ${displayName}? They will go back to a regular student account.`;
    }
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.loadUsers(true);
      }, 350);
    }
  },
  created() {
    this.$store.dispatch('fetchAdminStats');
  },
  methods: {
    handleTabNav(tabKey) {
      this.activeTab = tabKey;
      if (tabKey === 'overview' && !this.adminStats) {
        this.$store.dispatch('fetchAdminStats');
      }
      if (tabKey === 'users' && this.adminUsers.length === 0) {
        this.loadUsers(true);
      }
    },
    loadUsers(reset) {
      this.$store.dispatch('fetchAdminUsersPage', {
        reset,
        searchQuery: this.searchQuery,
        filters: this.filters
      });
    },
    loadMoreUsers() {
      this.$store.dispatch('fetchAdminUsersPage', {
        searchQuery: this.searchQuery,
        filters: this.filters
      });
    },
    applyFilters(newFilters) {
      this.filters = newFilters;
      this.loadUsers(true);
    },
    handleToggleRole(rowUser) {
      this.pendingRoleChange = {
        uid: rowUser.id,
        name: `${rowUser.name || ''} ${rowUser.surname || ''}`.trim(),
        email: rowUser.email,
        newRole: rowUser.role === 'admin' ? 'user' : 'admin'
      };
      this.showRoleConfirm = true;
    },
    confirmRoleChange() {
      if (!this.pendingRoleChange) return;
      this.$store.dispatch('updateAdminUserRole', {
        uid: this.pendingRoleChange.uid,
        newRole: this.pendingRoleChange.newRole
      });
      this.pendingRoleChange = null;
    }
  }
};
</script>
